import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type {
  QueryClient,
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@unifyapps/network/react-query';
import { useQuery } from '@unifyapps/network/react-query';
import type { ErrorType } from '@unifyapps/network/utils/fetch';
import { executeFetch } from '@unifyapps/network/utils/fetch';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same as in the generated hooks
type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

export const getAuthDeployedInterface = (
  options?: SecondParameter<typeof executeFetch>,
  signal?: AbortSignal,
) => {
  return executeFetch<InterfaceEntity>(
    {
      url: `/auth/interface/deployed`,
      method: 'GET',
      signal,
    },
    options,
  );
};

export const getAuthDeployedInterfaceQueryKey = () => {
  return [`/auth/interface/deployed`] as const;
};

export const getAuthDeployedInterfaceQueryOptions = <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterface>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterface>>, TError, TData>
  >;
  request?: SecondParameter<typeof executeFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAuthDeployedInterfaceQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAuthDeployedInterface>>> = ({
    signal,
    meta,
  }) =>
    getAuthDeployedInterface(
      {
        ...requestOptions,
        meta,
      },
      signal,
    );

  return { queryKey, queryFn, staleTime: 5000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAuthDeployedInterface>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAuthDeployedInterfaceQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAuthDeployedInterface>>
>;
export type GetAuthDeployedInterfaceQueryError = ErrorType<unknown>;

export const useGetAuthDeployedInterface = <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterface>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterface>>, TError, TData>
  >;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getAuthDeployedInterfaceQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const prefetchGetAuthDeployedInterface = async <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterface>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterface>>, TError, TData>
    >;
  },
): Promise<QueryClient> => {
  const queryOptions = getAuthDeployedInterfaceQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export const getAuthDeployedInterfaceById = (
  id: string,
  options?: SecondParameter<typeof executeFetch>,
  signal?: AbortSignal,
) => {
  return executeFetch<InterfaceEntity>(
    {
      url: `/auth/interface/deployed/${id}`,
      method: 'GET',
      signal,
    },
    options,
  );
};

export const getAuthDeployedInterfaceByIdQueryKey = (id: string) => {
  return [`/auth/interface/deployed/${id}`] as const;
};

export const getAuthDeployedInterfaceByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>, TError, TData>
    >;
    request?: SecondParameter<typeof executeFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAuthDeployedInterfaceByIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>> = ({
    signal,
    meta,
  }) =>
    getAuthDeployedInterfaceById(
      id,
      {
        ...requestOptions,
        meta,
      },
      signal,
    );

  return { queryKey, queryFn, staleTime: 5000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAuthDeployedInterfaceByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>
>;
export type GetAuthDeployedInterfaceByIdQueryError = ErrorType<unknown>;

export const useGetAuthDeployedInterfaceById = <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>, TError, TData>
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getAuthDeployedInterfaceByIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const prefetchGetAuthDeployedInterfaceById = async <
  TData = Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthDeployedInterfaceById>>, TError, TData>
    >;
  },
): Promise<QueryClient> => {
  const queryOptions = getAuthDeployedInterfaceByIdQueryOptions(id, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * Prefetches the deployed auth interface record by id and returns it straight from the cache
 * (undefined if the fetch yielded nothing). Sits next to `useGetAuthDeployedInterfaceById` /
 * `prefetchGetAuthDeployedInterfaceById` / `getAuthDeployedInterfaceByIdQueryKey` so the prefetch,
 * the read, and the client hook that all key off the same id are colocated. Callers wanting a
 * degrade-on-failure policy should wrap this in their own try/catch.
 */
export async function fetchAuthDeployedInterfaceById<TData>(
  queryClient: QueryClient,
  id: string,
): Promise<TData | undefined> {
  await prefetchGetAuthDeployedInterfaceById(queryClient, id);
  return queryClient.getQueryData<TData>(getAuthDeployedInterfaceByIdQueryKey(id));
}

export const getAuthSavedInterface = (
  options?: SecondParameter<typeof executeFetch>,
  signal?: AbortSignal,
) => {
  return executeFetch<InterfaceEntity>(
    {
      url: `/auth/interface`,
      method: 'GET',
      signal,
    },
    options,
  );
};

export const getAuthSavedInterfaceQueryKey = () => {
  return [`/auth/interface`] as const;
};

export const getAuthSavedInterfaceQueryOptions = <
  TData = Awaited<ReturnType<typeof getAuthSavedInterface>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAuthSavedInterface>>, TError, TData>
  >;
  request?: SecondParameter<typeof executeFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAuthSavedInterfaceQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAuthSavedInterface>>> = ({
    signal,
    meta,
  }) =>
    getAuthSavedInterface(
      {
        ...requestOptions,
        meta,
      },
      signal,
    );

  return { queryKey, queryFn, staleTime: 5000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAuthSavedInterface>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAuthSavedInterfaceQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAuthSavedInterface>>
>;
export type GetAuthSavedInterfaceQueryError = ErrorType<unknown>;

export const useGetAuthSavedInterface = <
  TData = Awaited<ReturnType<typeof getAuthSavedInterface>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getAuthSavedInterface>>, TError, TData>
  >;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getAuthSavedInterfaceQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const prefetchGetAuthSavedInterface = async <
  TData = Awaited<ReturnType<typeof getAuthSavedInterface>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthSavedInterface>>, TError, TData>
    >;
  },
): Promise<QueryClient> => {
  const queryOptions = getAuthSavedInterfaceQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};
